// import components
import { Tooltip, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

// import external libraries
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

// import constants
import {
  ALL_TILE_ICON,
  BACKLOG_CATEGORIES,
  IMPLEMENTED_CATEGORIES
} from '../../Backlog.consts';

// import utils
import { categoryAppliesTo } from '../../Backlog.utils';

// import styles
import {
  TileButton,
  TileCount,
  TileIcon,
  TileLabel,
  TileSeparator,
  TilesContainer,
  TileValueRow
} from './BacklogCategoryTiles.styles';

// import types
import type { BacklogCategoryTilesProps } from './BacklogCategoryTiles.types';

const BacklogCategoryTiles = ({
  categoryCounts,
  activeCategories,
  rowEntityType,
  onToggleCategory,
  onToggleAll
}: BacklogCategoryTilesProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'backlog' });

  // The categories this view can actually report on — the same test each tile below applies to itself.
  // Without it the All tile speaks for tiles the planner cannot select: it reads as pressed because a
  // category kept from another view is still on, counts findings from tiles that are disabled, and its
  // toggle takes the deselect-everything branch, clearing the one tile that did apply.
  const applicableCategories = IMPLEMENTED_CATEGORIES.filter((category) =>
    categoryAppliesTo(category, rowEntityType)
  );

  const allActive =
    applicableCategories.length > 0 &&
    applicableCategories.every((category) =>
      activeCategories.includes(category)
    );
  // Only what was asked for: a deselected category was never detected, so it has no count to add.
  const totalCount = applicableCategories
    .filter((category) => activeCategories.includes(category))
    .reduce((total, category) => total + (categoryCounts[category] ?? 0), 0);

  // Only the categories with detection behind them, read alphabetically by label. A tile whose backend
  // has not landed carries no count and cannot be pressed, so it says nothing a planner can act on — and
  // this page goes to the customer. The metadata stays in BACKLOG_CATEGORIES: a tile comes back by
  // flipping its isImplemented.
  const orderedCategories = BACKLOG_CATEGORIES.filter(
    ({ isImplemented }) => isImplemented
  ).sort((first, second) =>
    t(`categories.${first.category}`).localeCompare(
      t(`categories.${second.category}`)
    )
  );

  return (
    <TilesContainer>
      <TileButton
        onClick={onToggleAll}
        isActive={allActive}
        aria-pressed={allActive}
        // aria-label replaces the accessible name, so the count has to be part of it or a screen
        // reader announces the tile with no number at all.
        aria-label={`${t('categories.all')}: ${totalCount}`}
      >
        <TileLabel noWrap>{t('categories.all')}</TileLabel>
        <TileValueRow>
          <TileIcon isActive={allActive} isAccent>
            <Icon type={ALL_TILE_ICON} />
          </TileIcon>
          <TileCount isActive={allActive}>{totalCount}</TileCount>
        </TileValueRow>
      </TileButton>

      <TileSeparator />

      {orderedCategories.map(({ category, iconType, iconColor }) => {
        // One reason a tile cannot be picked now that the unbacked ones are gone: it describes nothing
        // about the row type on screen. It reads as disabled, and the tooltip says why.
        const applies = categoryAppliesTo(category, rowEntityType);
        const isActive = applies && activeCategories.includes(category);
        const label = t(`categories.${category}`);
        // A category that was not requested was not detected, so there is no count to show — a dash
        // rather than a 0, which would claim there is nothing to do.
        const count = isActive ? categoryCounts[category] ?? 0 : null;
        const disabledReason = t('categories.notForThisEntityType');
        const tile = (
          <TileButton
            key={category}
            onClick={() => onToggleCategory(category)}
            disabled={!applies}
            isActive={isActive}
            aria-pressed={isActive}
            // The count (or the in-progress state for a not-yet-backed category) has to live in the
            // aria-label, since it replaces rather than adds to the accessible name.
            aria-label={`${label}: ${
              count === null ? (applies ? '–' : disabledReason) : count
            }`}
          >
            <TileLabel noWrap>{label}</TileLabel>
            <TileValueRow>
              <TileIcon isActive={isActive} color={iconColor}>
                <Icon type={iconType} />
              </TileIcon>
              {/* No number for a category nobody looked at — inventing one would mislead. */}
              <TileCount isActive={isActive}>
                {count === null ? '–' : count}
              </TileCount>
            </TileValueRow>
          </TileButton>
        );

        if (applies) return tile;

        return (
          <Tooltip key={category} title={disabledReason} arrow>
            {/* A disabled button fires no events, so the tooltip needs a wrapper to hang on. */}
            <Typography component="span" display="inline-flex">
              {tile}
            </Typography>
          </Tooltip>
        );
      })}
    </TilesContainer>
  );
};

export default memo(BacklogCategoryTiles);
